import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { IMAGES } from "../../lib/api";
import { MaskedLine, EASE } from "../motion";
import { useLang } from "../../lib/i18n";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen grain-overlay overflow-hidden bg-cream" data-testid="hero-section">
      <div className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full bg-beige/70 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-36 lg:pt-44 pb-20 grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            key={`kicker-${t("hero.kicker")}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
            className="flex items-center gap-3 text-[12px] tracking-[0.28em] uppercase text-taupe-dark mb-8"
            data-testid="hero-kicker"
          >
            <Sparkles className="w-4 h-4 text-gold" strokeWidth={1.5} />
            {t("hero.kicker")}
          </motion.p>
          <h1 className="font-display tracking-tight text-espresso text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.98]">
            <MaskedLine delay={0.15}>{t("hero.l1")}</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="italic text-taupe">{t("hero.l2")}</span>
            </MaskedLine>
            <MaskedLine delay={0.45}>{t("hero.l3")}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
            className="mt-8 max-w-md text-base md:text-lg text-espresso/70 font-light leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t("hero.sub")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#rendez-vous"
              data-testid="hero-booking-btn"
              className="rounded-full bg-taupe text-cream px-8 py-4 text-sm tracking-[0.12em] uppercase hover:bg-taupe-dark hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
            >
              {t("hero.cta1")}
            </a>
            <a
              href="#soins"
              data-testid="hero-discover-btn"
              className="group flex items-center gap-2 text-sm tracking-[0.12em] uppercase text-espresso/70 hover:text-espresso transition-colors duration-300"
            >
              {t("hero.cta2")}
              <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform duration-300" strokeWidth={1.5} />
            </a>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, clipPath: "inset(8% 8% 8% 8% round 24px)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0% round 24px)" }}
            transition={{ duration: 1.3, delay: 0.6, ease: EASE }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="overflow-hidden rounded-3xl spotlight-img">
              <motion.img
                src={IMAGES.treatment}
                alt="Nadou Skin"
                style={{ scale: imgScale }}
                className="w-full h-[420px] lg:h-[560px] object-cover"
                data-testid="hero-image"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
              className="absolute bottom-5 left-5 right-5 sm:right-auto bg-white/90 backdrop-blur-xl border border-taupe/20 rounded-2xl px-6 py-5 shadow-[0_8px_30px_rgba(44,36,27,0.08)]"
              data-testid="hero-badge"
            >
              <p className="font-display text-2xl text-espresso">{t("hero.badgeTitle")}</p>
              <p className="text-xs tracking-[0.18em] uppercase text-taupe-dark mt-1">{t("hero.badgeSub")}</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
